import * as L from 'leaflet'
import weatherWind from '@map-sdk/sdk/plugins/weather/Weather/WeatherWind'
import { weatherArrow } from '@map-sdk/sdk/plugins/weather/Weather/WeatherArrow'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'
import type { WeatherWindOptions } from '@map-sdk/sdk/plugins/weather/Weather/WeatherWind'
import type { WeatherArrowOptions } from '@map-sdk/sdk/plugins/weather/Weather/WeatherArrow'

export class WeatherSymbolHandler {
  private _symbolLayer: L.LayerGroup
  private _map: MyMap

  constructor(map: MyMap, layer: L.LayerGroup) {
    this._map = map
    this._symbolLayer = layer
  }

  private processWeatherData(
    datas: { x: number; y: number; value: string }[]
  ): void {
    for (const data of datas) {
      if (data) {
        const longitude = data.x / 1000
        const latitude = data.y / 1000
        const latLng = L.latLng(latitude, longitude)
        if (this._map.getBounds().contains(latLng)) {
          const [speedValue, directionValue] = data.value.split('/')
          if (
            !Number.isNaN(Number.parseInt(speedValue)) &&
            !Number.isNaN(Number.parseInt(directionValue)) &&
            Number.parseInt(speedValue) !== -32767 &&
            Number.parseInt(directionValue) !== -32767
          ) {
            const options: WeatherWindOptions = {
              direction: Number.parseInt(directionValue),
              size: 22,
              speed: Number.parseInt(speedValue),
            }
            this._symbolLayer.addLayer(weatherWind(latLng, options))
          }
        }
      }
    }
  }

  /**
   * 风速和风向
   * @param datas
   */
  private processWindData<T>(datas: T[]) {
    const filteredData = this.filterDataByZoomLevel(datas, 6, false)
    for (const point of filteredData) {
      if (point) {
        const lat = point.y / 1000
        const lng = point.x / 1000
        const location = L.latLng(lat, lng)
        if (this._map.getBounds().contains(location)) {
          const [speedStr, directionStr] = point.value.split('/')
          if (
            Number.parseInt(speedStr) !== -32768 &&
            Number.parseInt(directionStr) !== -32768
          ) {
            const options = {
              direction: directionStr,
              size: 22,
              speed: speedStr,
            }
            const windLayer = weatherWind(location, options)
            this._symbolLayer.addLayer(windLayer)
          }
        }
      }
    }
  }
  /**
   * 风速和风向，带有交互式箭头
   * @param datas
   */
  private processInteractiveWindArrows<T>(datas: T[]) {
    const filteredData = this.filterDataByZoomLevel(datas, 6, false)
    for (const point of filteredData) {
      if (point) {
        const lat = point.y / 1000
        const lng = point.x / 1000
        const location = L.latLng(lat, lng)
        if (this._map.getBounds().contains(location)) {
          const [speedStr, directionStr] = point.value.split('/')
          if (
            Math.abs(Number.parseInt(speedStr)) !== 32768 &&
            Math.abs(Number.parseInt(directionStr)) !== 32768 &&
            Number.parseFloat(speedStr) > 0.012
          ) {
            const speed = Number.parseFloat(speedStr) * 100
            const sizeIndex =
              speed < 1
                ? 0
                : [12, 15, 18, 21, 30, 27, 30, 33][
                    Math.min(Math.floor(speed / 14), 7)
                  ]
            const direction = Math.round(Number.parseFloat(directionStr) + 180)
            const arrowOptions: WeatherArrowOptions = {
              weight: 0.1,
              color: '#329808',
              direction,
              size: sizeIndex,
              item: {
                speed: speedStr,
                direction,
                direction_old: directionStr,
              },
            }
            const arrowLayer = weatherArrow(location, arrowOptions)
            this._symbolLayer.addLayer(arrowLayer)
            arrowLayer.on('click', (event) => {
              console.debug(event)
            })
          }
        }
      }
    }
  }
  /**
   * 显示数值标记
   * @param datas
   */
  private processNumberMarkers<T>(datas: T[]) {
    const filteredData = this.filterDataByZoomLevel(datas, 7, true)
    for (const point of filteredData) {
      if (point) {
        const lat = point.y / 1000
        const lng = point.x / 1000
        const location = L.latLng(lat, lng)
        if (this._map.getBounds().contains(location)) {
          const value = point.value
          if (Number.parseInt(value) !== -32768) {
            const marker = L.marker(location, {
              icon: L.divIcon({
                html: `${Math.floor(value)}`,
                className: 'weather-label',
                iconSize: [40, 20],
              }),
            })
            this._symbolLayer.addLayer(marker)
          }
        }
      }
    }
  }

  public displayWeatherData<T>(weatherData: T[], dataType: number): void {
    switch (dataType) {
      case 13: // 风速和风向
        this.processWindData(weatherData)
        break
      case 15: // 风速和风向，带有交互式箭头
        this.processInteractiveWindArrows(weatherData)
        break
      case 16: // 显示数值标记
        this.processNumberMarkers(weatherData)
        break
      default:
        console.error('error')
    }
  }

  public updateWeatherSymbols(processedData: any[]): void {
    this.processWeatherData(processedData)
  }

  private filterDataByZoomLevel(
    data: string | any[],
    minZoom: number,
    specialCondition: any
  ) {
    const zoom = this._map.getZoom()
    let stepX = 0
    let stepY = 0
    switch (zoom) {
      case 6:
        stepX = 2
        stepY = 2
        break
      case 5:
        stepX = 4
        stepY = 3
        break
      case 4:
        if (specialCondition) {
          stepX = 6
          stepY = 5
        } else {
          stepX = 4
          stepY = 3
        }
        break
      case 3:
        if (specialCondition) {
          stepX = 8
          stepY = 13
        } else {
          stepX = 6
          stepY = 11
        }
        break
      case 2:
        if (specialCondition) {
          stepX = 13
          stepY = 20
        } else {
          stepX = 8
          stepY = 13
        }
        break
      case 1:
        stepX = 10
        stepY = 15
    }

    if (zoom < minZoom) {
      const xData = data.slice(0, data.length / 2)
      const yData = data.slice(data.length / 2)
      const filteredXData: any[] = []
      const filteredYData: any[] = []

      for (let i = 0; i < 170; i += stepX) {
        filteredXData.push(xData.slice(420 * i, 420 * (i + 1)))
        filteredYData.push(yData.slice(420 * i, 420 * (i + 1)))
      }

      const result: any[] = []
      filteredXData.forEach((chunkX) => {
        chunkX.forEach((itemX: any, innerIndex: number) => {
          if (innerIndex % stepY === 0) {
            result.push(itemX)
          }
        })
      })

      filteredYData.forEach((chunkY) => {
        chunkY.forEach((itemY: any, innerIndex: number) => {
          if (innerIndex % stepY === 0) {
            result.push(itemY)
          }
        })
      })

      return result
    }

    return data
  }
}

// 使用示例
// const map = L.map('mapid').setView([51.505, -0.09], 13);
// const weatherSymbolHandler = new WeatherSymbolHandler(map);
// const processedData = weatherDataProcessor.processData(6, false);
// weatherSymbolHandler.updateWeatherSymbols(processedData);
